/**
 * Camera-permission analytics for the menu-scan capture flow.
 *
 * The Capacitor Camera plugin requests OS permission implicitly inside Camera.getPhoto, so there is
 * no single call we can wrap. These helpers read the permission state around a getPhoto call to emit
 * the camera_permission funnel step:
 *   - `prompted` — the OS dialog is about to appear (state is still "prompt").
 *   - `granted`  — logged by the caller AFTER getPhoto resolves (access was allowed).
 *   - `denied`   — logged from the getPhoto catch, but only when permission is actually blocked
 *                  (so a user cancelling the camera is NOT counted as a denial).
 *
 * Native (iOS/Android) only — the web fallback uses a file input with no OS permission gate.
 */
import { Camera } from '@capacitor/camera'
import { logEvent } from '../analytics/analytics'
import { errorLog } from '../utils/log-helper'

/**
 * Emit camera_permission=prompted when the OS is about to show the permission dialog. Call it
 * immediately before Camera.getPhoto with the Camera source. No-op if permission is already granted
 * or already denied (nothing will be prompted).
 */
export async function emitCameraPromptIfNeeded(screenName: string): Promise<void> {
  try {
    const { camera } = await Camera.checkPermissions()
    if (camera === 'prompt' || camera === 'prompt-with-rationale') {
      logEvent('camera_permission', { status: 'prompted', screen_name: screenName })
    }
  } catch (e) {
    errorLog('[camera-analytics] checkPermissions failed', e)
  }
}

/**
 * After a Camera.getPhoto rejection, emit camera_permission=denied only when the failure was an
 * actual permission block — not a user cancel. Call it from the getPhoto catch on native.
 */
export async function emitCameraDeniedIfDenied(screenName: string): Promise<void> {
  try {
    const { camera } = await Camera.checkPermissions()
    if (camera === 'denied') {
      logEvent('camera_permission', { status: 'denied', screen_name: screenName })
    }
  } catch (e) {
    errorLog('[camera-analytics] checkPermissions failed', e)
  }
}
